import type { CreateReturnRequestDto } from "@babackapp/private-api-client/generated/schema";
import type { ReturnDraft } from "../types";
import { getRebuyItemsForSubmission } from "./draft.lib";

export function buildReturnRequestPayload(draft: ReturnDraft): CreateReturnRequestDto {
  const isRetractation = draft.returnMode === "retractation";
  const rebuyItems = getRebuyItemsForSubmission(draft);

  return {
    orderId: draft.orderId,
    shopId: draft.shopId,
    // Only a merchant can uncheck it; the server forces a paid return for the portal.
    isPaidReturn: draft.returnShippingPaid ?? true,
    withShippingLabel: isRetractation ? false : (draft.withShippingLabel ?? true),
    isRetractation: isRetractation || undefined,
    lineItems: draft.lineItems.map((li) => ({
      lineItemId: li.lineItemId,
      quantity: li.quantity,
      reasonId: li.reasonId ?? "",
      resolution: (li.resolution ?? "refund") as CreateReturnRequestDto["lineItems"][number]["resolution"],
      // A variant picked then left for another resolution stays in the draft
      // ("Other sizes…", then Back, then Refund): only an exchange sends it.
      newVariantId:
        li.resolution === "exchange" ? (li.exchangeVariantId ?? undefined) : undefined,
      comment: li.comment ?? undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      customerImages: li.imageUrls.map((image) => ({
        id: image.imageId,
        name: image.imageId,
        size: "0",
        url: image.url,
      })) as any,
    })),
    returnStoreId: draft.storeId ?? undefined,
    shippingAddress: draft.address!,
    pickingTiming: draft.pickupDate
      ? { pickupTimeStart: 0, pickupTimeEnd: 0, instruction: draft.pickupDate }
      : undefined,
    intentId: draft.paymentIntentId ?? undefined,
    newArticles: rebuyItems.length
      ? (rebuyItems.map((item) => ({
          id: String(item.variantId),
          variantId: String(item.variantId),
          quantity: item.quantity,
          price: item.price / 100,
          title: item.title,
          image: item.image ?? null,
          sku: item.sku,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        })) as any)
      : undefined,
    rebuyRemainingCreditMethod: draft.rebuyRemainingCreditMethod ?? undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paidReturnMethod: (draft.paidReturnMethod ?? undefined) as any,
    shippingLine: (!isRetractation && draft.shippingRateId && draft.shippingRateTitle
      ? {
          code: draft.shippingRateId,
          title: draft.shippingRateTitle,
          price: String((draft.shippingRateAmount ?? 0) / 100),
        }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      : undefined) as any,
    notifications: { consumerEnabled: draft.notifyCustomer ?? true },
  };
}
